/** The seam between the React shell and the simulation.
 *
 * The shell never holds simulation state (D8): it holds a *reference* to a store
 * the host owns, and re-reads through it whenever `version` changes. That is the
 * only reason a `GameStore` interface exists rather than a context full of props.
 *
 * The store is also where legality lives. The UI may not call a rule module
 * (I10), so `check` is implemented by the host - typically by handing the command
 * to `enumerateLegalCommands` / `executeCommand` with `verify` - and the shell
 * only ever reads the structured `Reason` that comes back.
 */

import { createContext, useContext } from "react";
import type { ContentRegistry, GameCommand, PlayerObservation, Reason } from "@game/core";

export interface GameStore {
  /** Called after every state change. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
  /** Monotonic. The only thing React subscribes to. */
  getVersion(): number;
  /**
   * Must be referentially stable for a given version: `useSyncExternalStore`
   * and every `useMemo` in this package key off it.
   */
  getObservation(): PlayerObservation;
  /** Null until the content bundle has loaded. */
  getContent(): ContentRegistry | null;
  /** Fire and forget; the resulting state change arrives through `subscribe`. */
  dispatch(command: GameCommand): void;
  /**
   * Why this command would be refused, or null if it would be accepted.
   * Implemented by the host against the kernel - never re-derived here.
   */
  check(command: GameCommand): Reason | null;
}

export const GameStoreContext = createContext<GameStore | null>(null);

export function useStore(): GameStore {
  const store = useContext(GameStoreContext);
  if (store === null) {
    throw new Error("no GameStore in context: wrap the screen in <GameStoreContext.Provider>");
  }
  return store;
}

/** Optional access, for a component that must render before the game exists. */
export function useStoreOrNull(): GameStore | null {
  return useContext(GameStoreContext);
}
